import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, ArrowUpRight, ArrowDownRight, ChevronDown, ChevronLeft, ChevronRight, Check, CalendarDays } from 'lucide-react'
import { useTypeahead } from '../../lib/useTypeahead.js'
import { bkkTodayISO } from '../../kit/format.js'

// ── constants ────────────────────────────────────────────────────────────────
export const TH_MON_ABBR = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
export const TH_DOW_ABBR = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.']
export const TH_MON_FULL = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']
export const MONTH_START = '2024-01-01'   // earliest real data
export const DAILY_LIMIT = 62             // top chart auto-switches to monthly past this
export const ORANGE = '#ea580c'
export const BLUE = '#2563eb'
export const GREEN = '#16a34a'
export const PURPLE = '#9333ea'
export const SLATE = '#94a3b8'
export const PALETTE = ['#2563eb', '#ea580c', '#16a34a', '#9333ea', '#db2777', '#0d9488', '#ca8a04', '#dc2626', '#0891b2', '#65a30d', '#7c3aed', '#e11d48', '#0ea5e9', '#f59e0b']
export const RADAR_PRESETS = ['thisMonth', '1mo']
export const PRESETS = [
  { key: 'thisMonth', label: 'เดือนนี้' },
  { key: '1mo', label: '1 เดือน' }, { key: '3mo', label: '3 เดือน' }, { key: '6mo', label: '6 เดือน' }, { key: '12mo', label: '12 เดือน' },
]
export const INPUT = 'rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-300'

// ── date helpers ─────────────────────────────────────────────────────────────
export const pad = (n) => String(n).padStart(2, '0')
export const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const parseISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d) }
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x }
export const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x }
export const daysBetween = (a, b) => Math.round((parseISO(b) - parseISO(a)) / 86400000)
// The clinic's today as 'YYYY-MM-DD' — Bangkok, never the browser's clock (they
// differ 7h of every UTC day, and all day for a viewer abroad).
export const todayISO = () => bkkTodayISO()
export const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('en-US')
// Xano list endpoints return { items: [...] }; bare arrays pass through.
export const asList = (r) => (Array.isArray(r) ? r : (r?.items || []))

export function enumDays(start, end) {
  const out = []
  for (let d = parseISO(start); toISO(d) <= end; d = addDays(d, 1)) out.push(toISO(d))
  return out
}
export function enumMonths(start, end) {
  const out = []
  const s = parseISO(start), e = parseISO(end)
  for (let y = s.getFullYear(), m = s.getMonth(); y < e.getFullYear() || (y === e.getFullYear() && m <= e.getMonth()); ) {
    out.push({ year: y, month: m + 1 })
    if (++m > 11) { m = 0; y++ }
  }
  return out
}
// bucket list (day or month) for a range — each {key,label,full}
export function bucketsFor(gran, start, end) {
  if (gran === 'month') {
    return enumMonths(start, end).map(({ year, month }) => ({
      key: `${year}-${pad(month)}`,
      label: `${TH_MON_ABBR[month - 1]} ${String(year).slice(2)}`,
      full: `${TH_MON_FULL[month - 1]} ${year}`,
    }))
  }
  return enumDays(start, end).map((iso) => {
    const d = parseISO(iso)
    return { key: iso, label: `${d.getDate()}/${d.getMonth() + 1}`, full: `${d.getDate()} ${TH_MON_ABBR[d.getMonth()]} ${d.getFullYear()}` }
  })
}

export function presetRange(key) {
  // anchor on the Bangkok day; parseISO gives a local-midnight Date of that day,
  // so the local-field arithmetic below stays on the clinic's calendar
  const now = parseISO(todayISO()), y = now.getFullYear(), m = now.getMonth()
  switch (key) {
    // multi-month presets snap the start to the 1st of that first month
    case 'thisMonth': return [toISO(new Date(y, m, 1)), toISO(now)]
    case '3mo': return [toISO(new Date(y, m - 3, 1)), toISO(now)]
    case '6mo': return [toISO(new Date(y, m - 6, 1)), toISO(now)]
    case '12mo': return [toISO(new Date(y, m - 12, 1)), toISO(now)]
    default: return [toISO(addMonths(now, -1)), toISO(now)] // '1mo'
  }
}
// equal-length window immediately before [start,end]
export function prevRange(start, end) {
  const days = daysBetween(start, end) + 1
  const pe = addDays(parseISO(start), -1)
  const ps = addDays(pe, -(days - 1))
  return [toISO(ps), toISO(pe)]
}
// shift an ISO date by whole years/months, clamping the day to the target month
// length (so e.g. May 31 → previous month becomes Apr 30, not May 1).
function shiftBy(iso, years, months) {
  const d = parseISO(iso)
  const y = d.getFullYear() + years
  const m = d.getMonth() + months
  const last = new Date(y, m + 1, 0).getDate()
  return toISO(new Date(y, m, Math.min(d.getDate(), last)))
}

// The comparison range depends on the preset:
//   เดือนนี้ (thisMonth) → the SAME day-span in the PREVIOUS MONTH
//   1 เดือน  (1mo)       → the PREVIOUS equal-length window (≈ previous 30 days)
//   3/6/12mo + custom    → the SAME span one YEAR earlier (year-over-year)
export function comparePrevRange(preset, start, end) {
  if (preset === 'thisMonth') return [shiftBy(start, 0, -1), shiftBy(end, 0, -1)]
  if (preset === '1mo') return prevRange(start, end)
  return [shiftBy(start, -1, 0), shiftBy(end, -1, 0)]
}

// Button + legend labels that match the comparison the preset actually performs.
export function compareLabel(preset) {
  if (preset === 'thisMonth') return 'เทียบเดือนก่อน'
  if (preset === '1mo') return 'เทียบ 30 วันก่อน'
  return 'เทียบปีก่อน'
}
export function comparePrevLabel(preset) {
  if (preset === 'thisMonth') return 'เดือนก่อน'
  if (preset === '1mo') return '30 วันก่อน'
  return 'ปีก่อน'
}

// ── presentational components ────────────────────────────────────────────────
export function Card({ icon, title, badge, subtitle, right, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">{icon}</div>
          <div>
            <h2 className="text-[15px] font-semibold text-slate-900 leading-tight flex items-center gap-2">
              {title}
              {badge && <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">{badge}</span>}
            </h2>
            <p className="text-[12.5px] text-slate-400 mt-0.5">{subtitle}</p>
          </div>
        </div>
        {right}
      </div>
      {children}
    </section>
  )
}

export function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
      {options.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-[12.5px] font-medium transition ${value === o.v ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          {o.dot && <span className="w-2 h-2 rounded-full" style={{ background: o.dot }} />}
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function Toggle({ on, onClick, children }) {
  return (
    <button onClick={onClick}
      className={`rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition ${on ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
      {children}
    </button>
  )
}

export function PresetBar({ preset, onPick }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-3">
      {PRESETS.map((p) => [
        // divider splits the star-chart presets (เดือนนี้/1 เดือน) from the line-chart ones
        p.key === '3mo' ? <span key="div" className="w-px h-5 bg-slate-200 mx-1" /> : null,
        <button key={p.key} onClick={() => onPick(p.key)}
          className={`rounded-full px-3 py-1 text-[12px] border transition ${preset === p.key ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
          {p.label}
        </button>,
      ])}
    </div>
  )
}

/* ── ช่วงวันที่ — the range picker ──────────────────────────────────────────────
 *
 * One field, one popover, two months side by side: click the first day, click
 * the last, done — the way a flight search asks for it. It replaces a pair of
 * native <input type="date"> boxes, which put the two ends of ONE question in
 * two separate controls and never showed how long the window was until after
 * the fetch came back.
 *
 * 🔑 THE RANGE IS COMMITTED ON THE SECOND CLICK, NEVER THE FIRST. Every page
 * that uses this refetches on [start, end], so committing the first click too
 * would fire a throwaway request for a one-day window on the way to every
 * selection someone makes. The half-made pick lives in local `anchor` state
 * until it is a real range. That matters most on /reports/newcase, whose
 * payload is every new HN in the window plus all of their appointments —
 * exactly the shape of request that took app.smileseasons.com down in August.
 * One fetch per selection, not two.
 *
 * `maxDays` (0 = no cap) is the longest window that may be picked, counted
 * INCLUSIVELY: 60 means "60 days counting both ends". While the far end is
 * being chosen, anything past the cap is unreachable rather than clickable and
 * then rejected upstream. A range saved before a cap existed is clamped on
 * mount, so the invariant also holds for state this control did not create.
 *
 * No day after `today` is selectable on either end — these are reports about
 * what has already happened, and `today` is the CLINIC's day (Bangkok), passed
 * in by the page rather than read from the browser.
 */
const monthIndex = (y, m) => y * 12 + (m - 1)
const monthOfISO = (iso) => ({ year: Number(String(iso).slice(0, 4)), month: Number(String(iso).slice(5, 7)) })
const shiftISO = (iso, n) => toISO(addDays(parseISO(iso), n))
// Calendar cells are days, not instants, so they are laid out in UTC — the
// viewer's timezone must never get a vote in which day lands in which column.
// Same arithmetic as components/lab/LabDayPicker.jsx.
function monthCells(year, month) {
  const lead = new Date(Date.UTC(year, month - 1, 1)).getUTCDay()
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const cells = new Array(lead).fill(null)
  for (let d = 1; d <= days; d++) cells.push(`${year}-${pad(month)}-${pad(d)}`)
  return cells
}
// 'YYYY-MM-DD' → '7 ก.ย. 26'. Gregorian two-digit year, matching fmtDay() in
// lib/newcase.js and the day column of every inspection report.
const fmtNice = (iso) => {
  const d = parseISO(iso)
  return `${d.getDate()} ${TH_MON_ABBR[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`
}

function MonthGrid({ year, month, lo, hi, isDisabled, onPick, onHover, today }) {
  const cells = useMemo(() => monthCells(year, month), [year, month])
  const single = lo === hi
  return (
    <div className="w-[15.5rem]">
      <div className="text-center text-[12.5px] font-semibold text-slate-700 tabular-nums pb-1.5">
        {TH_MON_ABBR[month - 1]} {year}
      </div>
      <div className="grid grid-cols-7 text-center">
        {TH_DOW_ABBR.map((d) => <div key={d} className="text-[10.5px] text-slate-400 pb-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((iso, i) => {
          if (!iso) return <div key={`x${i}`} className="h-8" />
          const off = isDisabled(iso)
          const isLo = iso === lo
          const isHi = iso === hi
          const inside = !!lo && !!hi && iso > lo && iso < hi
          // The band is painted by the WRAPPER so it runs edge to edge between
          // cells; the round endpoint is the button on top of it. A one-day
          // range paints no band at all.
          const band = !single && (inside || isLo || isHi)
          return (
            <div key={iso}
              className={`h-8 ${band ? 'bg-brand-50' : ''} ${!single && isLo ? 'rounded-l-full' : ''} ${!single && isHi ? 'rounded-r-full' : ''}`}>
              <button
                type="button"
                disabled={off}
                onClick={() => onPick(iso)}
                onMouseEnter={() => onHover(iso)}
                aria-label={fmtNice(iso)}
                aria-current={isLo || isHi ? 'date' : undefined}
                className={`w-full h-8 rounded-full text-[12.5px] tabular-nums transition ${
                  off ? 'text-slate-300 cursor-not-allowed'
                    : (isLo || isHi) ? 'bg-brand-600 text-white font-medium shadow-sm'
                      : inside ? 'text-brand-700'
                        : `text-slate-700 hover:bg-slate-100 ${iso === today ? 'ring-1 ring-inset ring-slate-300' : ''}`
                }`}>
                {Number(iso.slice(8))}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function DateRange({ start, end, today = todayISO(), onStart, onEnd, maxDays = 0 }) {
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState(null)   // first click, not yet committed
  const [hover, setHover] = useState(null)
  const [cursor, setCursor] = useState(() => monthOfISO(start || today))  // the LEFT month
  const btnRef = useRef(null)
  // Two months is ~615px of popover. This control sits at the LEFT of a filter
  // bar on the report pages and at the RIGHT of a Card header on the stat ones,
  // so which edge it hangs from is measured rather than guessed or passed in.
  const [alignRight, setAlignRight] = useState(false)

  // A window saved before this cap existed would otherwise stay over-length
  // forever, because nothing else ever revisits it. Fires once: after the
  // clamp the condition is false. `onEnd` is deliberately not a dependency —
  // pages pass an inline lambda, and re-running on its identity would loop.
  useEffect(() => {
    if (!maxDays || !start || !end) return
    if (daysBetween(start, end) + 1 > maxDays) onEnd(shiftISO(start, maxDays - 1))
  }, [maxDays, start, end])   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !btnRef.current) return
    const r = btnRef.current.getBoundingClientRect()
    setAlignRight(r.left + 620 > window.innerWidth)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); setAnchor(null); setHover(null) } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const span = daysBetween(start, end) + 1
  // While the far end is being chosen the band follows the cursor, so the
  // length of the window is visible before it is committed.
  const lo = anchor || start
  const hi = anchor ? (hover && hover > anchor ? hover : anchor) : end
  const capEnd = anchor && maxDays ? shiftISO(anchor, maxDays - 1) : ''
  // A day BEFORE the anchor is never disabled by the cap — clicking it restarts
  // the selection from there, which is the one escape hatch from a bad first click.
  const isDisabled = (iso) => iso > today || (!!capEnd && iso > capEnd)

  const openPicker = () => {
    setCursor(monthOfISO(start || today))
    setAnchor(null); setHover(null); setOpen(true)
  }
  const close = () => { setOpen(false); setAnchor(null); setHover(null) }
  const commit = (s, e) => { onStart(s); onEnd(e); close() }

  const pick = (iso) => {
    if (isDisabled(iso)) return
    if (!anchor || iso < anchor) { setAnchor(iso); setHover(null); return }
    commit(anchor, iso)
  }

  const tM = monthOfISO(today)
  const leftIdx = monthIndex(cursor.year, cursor.month)
  const canNext = leftIdx + 1 < monthIndex(tM.year, tM.month)   // the right month may reach today's, not pass it
  const step = (delta) => setCursor(({ year, month }) => {
    const i = monthIndex(year, month) + delta
    return { year: Math.floor(i / 12), month: (i % 12) + 1 }
  })
  const rightIdx = leftIdx + 1
  const rightMonth = { year: Math.floor(rightIdx / 12), month: (rightIdx % 12) + 1 }

  // Spans people actually ask for, committed in one click. Any preset longer
  // than the cap is simply not offered rather than shown and refused.
  const quick = [
    { label: 'วันนี้', days: 1 },
    { label: '7 วัน', days: 7 },
    { label: '30 วัน', days: 30 },
    { label: 'เดือนนี้', days: Number(today.slice(8)) },
  ].filter((q, i, all) => q.days >= 1
    && (!maxDays || q.days <= maxDays)
    // on the 1st of a month เดือนนี้ IS วันนี้ — don't offer the same span twice
    && all.findIndex((o) => o.days === q.days) === i)

  return (
    <div className="relative">
      <button ref={btnRef} type="button" onClick={() => (open ? close() : openPicker())}
        aria-haspopup="dialog" aria-expanded={open}
        className={`${INPUT} inline-flex items-center gap-2 text-left ${open ? 'ring-2 ring-brand-100 border-brand-300' : ''}`}>
        <CalendarDays size={14} className="text-slate-400 shrink-0" />
        <span className="tabular-nums whitespace-nowrap">
          {start === end ? fmtNice(start) : `${fmtNice(start)} – ${fmtNice(end)}`}
        </span>
        <span className="text-[12px] text-slate-400 tabular-nums whitespace-nowrap">· {fmt(span)} วัน</span>
        <ChevronDown size={14} className="text-slate-400 shrink-0" />
      </button>

      {open && (
        <>
          <button className="fixed inset-0 z-10 cursor-default" onClick={close} aria-hidden tabIndex={-1} />
          <div role="dialog" aria-label="เลือกช่วงวันที่"
            className={`absolute z-20 mt-1 ${alignRight ? 'right-0' : 'left-0'} rounded-xl border border-slate-200 bg-white shadow-lg p-3 max-w-[calc(100vw-2rem)]`}>
            <div className="flex flex-wrap items-center gap-1.5 pb-2.5">
              {quick.map((q) => (
                <button key={q.label} type="button"
                  onClick={() => commit(shiftISO(today, -(q.days - 1)), today)}
                  className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[12px] text-slate-600 hover:border-brand-300 hover:text-brand-700">
                  {q.label}
                </button>
              ))}
            </div>

            <div className="flex items-start gap-4">
              <button type="button" onClick={() => step(-1)}
                aria-label="เดือนก่อนหน้า"
                className="p-1 rounded text-slate-400 hover:text-brand-600 hover:bg-slate-50 shrink-0">
                <ChevronLeft size={16} />
              </button>
              <div className="flex flex-wrap gap-4" onMouseLeave={() => setHover(null)}>
                <MonthGrid {...cursor} lo={lo} hi={hi} today={today} isDisabled={isDisabled} onPick={pick} onHover={setHover} />
                <MonthGrid {...rightMonth} lo={lo} hi={hi} today={today} isDisabled={isDisabled} onPick={pick} onHover={setHover} />
              </div>
              <button type="button" onClick={() => canNext && step(1)} disabled={!canNext}
                aria-label="เดือนถัดไป"
                className="p-1 rounded text-slate-400 hover:text-brand-600 hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 shrink-0">
                <ChevronRight size={16} />
              </button>
            </div>

            <div className="flex items-center justify-between gap-3 pt-2.5 mt-2.5 border-t border-slate-100">
              <span className="text-[11.5px] text-slate-400">
                {anchor
                  ? <>เลือกวันสิ้นสุด{capEnd && capEnd < today && <> · ได้ถึง <span className="tabular-nums text-slate-500">{fmtNice(capEnd)}</span></>}</>
                  : <>เลือกช่วง <span className="tabular-nums text-slate-500">{fmt(span)} วัน</span>{maxDays ? ` · สูงสุด ${maxDays} วัน` : ''}</>}
              </span>
              <button type="button" onClick={close} className="text-[12px] text-slate-500 hover:underline shrink-0">ปิด</button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// Multi-select dropdown (sources, types …). enabled is a Set of selected ids.
export function MultiSelect({ label, items, enabled, onToggle, onAll, onClear, disabled, note, search }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const on = items.reduce((n, it) => (enabled.has(it.id) ? n + 1 : n), 0)
  const filtered = search && q ? items.filter((it) => it.label.toLowerCase().includes(q.toLowerCase())) : items
  // Keyboard nav over the currently rendered (filtered) rows; Enter toggles the
  // active row and keeps the dropdown open (multi-select), Escape closes it.
  const ta = useTypeahead({
    count: filtered.length,
    onSelect: (i) => filtered[i] && onToggle(filtered[i].id),
    onClose: () => setOpen(false),
  })
  return (
    <div className={`relative ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <button onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12.5px] text-slate-700 hover:border-slate-300">
        <span className="text-slate-500">{label}</span>
        <span className="font-medium tabular-nums">{on}/{items.length}</span>
        <ChevronDown size={14} className="text-slate-400" />
      </button>
      {note && <span className="ml-1.5 text-[11.5px] text-slate-400">{note}</span>}
      {open && (
        <>
          <button className="fixed inset-0 z-10 cursor-default" onClick={() => setOpen(false)} aria-hidden tabIndex={-1} />
          <div className="absolute z-20 mt-1 w-64 rounded-xl border border-slate-200 bg-white shadow-lg p-2">
            <div className="flex items-center justify-between px-1 pb-1.5">
              <button onClick={onAll} className="text-[12px] text-brand-600 hover:underline">ทั้งหมด</button>
              <button onClick={onClear} className="text-[12px] text-slate-500 hover:underline">ล้าง</button>
            </div>
            {search && (
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={ta.onKeyDown} placeholder="ค้นหา…"
                className="w-full mb-1.5 rounded-md border border-slate-200 px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-brand-100" />
            )}
            <div ref={ta.listRef} className="max-h-56 overflow-y-auto -mr-1 pr-1">
              {filtered.map((it, i) => {
                const o = enabled.has(it.id)
                return (
                  <button key={it.id} data-ti-index={i} onClick={() => onToggle(it.id)} onMouseEnter={() => ta.setActiveIndex(i)}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-[12.5px] ${i === ta.activeIndex ? 'bg-slate-50' : 'hover:bg-slate-50'}`}>
                    <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${o ? 'bg-brand-600 border-brand-600' : 'border-slate-300'}`}>
                      {o && <Check size={11} className="text-white" strokeWidth={3} />}
                    </span>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: it.color }} />
                    <span className="truncate text-slate-700">{it.label}</span>
                  </button>
                )
              })}
              {!filtered.length && <div className="px-2 py-3 text-center text-[12px] text-slate-400">ไม่พบ</div>}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// Generic filter chip row (branches, sources, types …). items: [{id,label,color}]
export function FilterChips({ label, items, enabled, onToggle, onAll, onClear, disabled, note, scroll }) {
  return (
    <div className={`flex flex-wrap items-center gap-2 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <span className="text-[12.5px] text-slate-500 mr-1">{label}</span>
      {note && <span className="text-[11.5px] text-slate-400">{note}</span>}
      <button onClick={onAll} className="text-[12px] text-brand-600 hover:underline">ทั้งหมด</button>
      <span className="text-slate-300">·</span>
      <button onClick={onClear} className="text-[12px] text-slate-500 hover:underline">ล้าง</button>
      <div className={`flex flex-wrap gap-1.5 ml-1 ${scroll ? 'max-h-[88px] overflow-y-auto' : ''}`}>
        {items.map((it) => {
          const on = enabled.has(it.id)
          return (
            <button key={it.id} onClick={() => onToggle(it.id)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition ${on ? 'border-slate-300 bg-white text-slate-700' : 'border-slate-200 bg-slate-50 text-slate-400'}`}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: on ? it.color : '#cbd5e1' }} />
              {it.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function Stat({ label, value, color }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />{label}
      </div>
      <div className="text-[19px] font-semibold text-slate-900 tabular-nums leading-tight">{value}</div>
    </div>
  )
}

export function Delta({ pct }) {
  const up = pct >= 0
  const Icon = up ? ArrowUpRight : ArrowDownRight
  return (
    <div className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[13px] font-semibold ${up ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
      <Icon size={15} strokeWidth={2.2} />{Math.abs(pct).toFixed(1)}%
    </div>
  )
}

export function ChartLoading() {
  return <div className="flex items-center justify-center gap-2 text-[13px] text-slate-400 h-[300px]"><Loader2 size={16} className="animate-spin" /> กำลังโหลด…</div>
}

export function Notice({ text, tone = 'warn' }) {
  const tones = { warn: 'bg-amber-50 text-amber-700 border-amber-200', error: 'bg-rose-50 text-rose-700 border-rose-200' }
  return <div className={`rounded-xl border px-4 py-3 text-[13px] ${tones[tone]} h-[300px] flex items-center justify-center text-center`}>{text}</div>
}
